import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Settings, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AdminMembershipPage = () => {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await adminAPI.getMembershipPlans();
      setPlans(response.data.plans);
    } catch (error) {
      toast.error('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const updatePlan = (planKey, field, value) => {
    setPlans({
      ...plans,
      [planKey]: {
        ...plans[planKey],
        [field]: field.includes('price') || field.includes('max') ? parseFloat(value) || 0 : value
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateMembershipPlans(plans);
      toast.success('Membership plans updated!');
    } catch (error) {
      toast.error('Failed to update plans');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Membership Plans
          </h1>
          <p className="text-[#5F6368]">Configure pricing and limits for each plan</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-google flex items-center gap-2"
          data-testid="save-plans-btn"
        >
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {plans && Object.entries(plans).map(([key, plan]) => (
          <div key={key} className="dashboard-card">
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                key === 'pro' ? 'bg-[#F3E8FD]' : key === 'basic' ? 'bg-[#E8F0FE]' : 'bg-[#F1F3F4]'
              }`}>
                <Settings className={`w-6 h-6 ${
                  key === 'pro' ? 'text-[#9334E9]' : key === 'basic' ? 'text-[#1A73E8]' : 'text-[#5F6368]'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                  {plan.name}
                </h3>
                <p className="text-sm text-[#5F6368]">{key.toUpperCase()} Plan</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Pricing */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1">Price (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={plan.price_usd}
                    onChange={(e) => updatePlan(key, 'price_usd', e.target.value)}
                    className="input-google py-2 text-sm"
                    data-testid={`${key}-price-usd`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1">Price (INR)</label>
                  <input
                    type="number"
                    value={plan.price_inr}
                    onChange={(e) => updatePlan(key, 'price_inr', e.target.value)}
                    className="input-google py-2 text-sm"
                    data-testid={`${key}-price-inr`}
                  />
                </div>
              </div>

              {/* Monthly Limits */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1">Posts/Month</label>
                  <input
                    type="number"
                    value={plan.max_posts_month}
                    onChange={(e) => updatePlan(key, 'max_posts_month', e.target.value)}
                    className="input-google py-2 text-sm"
                    data-testid={`${key}-posts-month`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1">Replies/Month</label>
                  <input
                    type="number"
                    value={plan.max_replies_month || 0}
                    onChange={(e) => updatePlan(key, 'max_replies_month', e.target.value)}
                    className="input-google py-2 text-sm"
                    data-testid={`${key}-replies-month`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1">Locations</label>
                  <input
                    type="number"
                    value={plan.max_locations || 1}
                    onChange={(e) => updatePlan(key, 'max_locations', e.target.value)}
                    className="input-google py-2 text-sm"
                    data-testid={`${key}-max-locations`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1">Templates</label>
                  <input
                    type="number"
                    value={plan.max_templates || 2}
                    onChange={(e) => updatePlan(key, 'max_templates', e.target.value)}
                    className="input-google py-2 text-sm"
                    data-testid={`${key}-max-templates`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1">Team Members</label>
                  <input
                    type="number"
                    value={plan.max_team_members || 0}
                    onChange={(e) => updatePlan(key, 'max_team_members', e.target.value)}
                    className="input-google py-2 text-sm"
                    data-testid={`${key}-max-team-members`}
                  />
                </div>
              </div>

              {/* Feature Toggles */}
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg">
                  <span className="text-sm font-medium text-[#202124]">Auto-Post</span>
                  <button
                    onClick={() => updatePlan(key, 'auto_post', !plan.auto_post)}
                    className={`w-10 h-5 rounded-full transition-colors ${
                      plan.auto_post ? 'bg-[#1A73E8]' : 'bg-[#DADCE0]'
                    }`}
                    data-testid={`${key}-auto-post`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      plan.auto_post ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg">
                  <span className="text-sm font-medium text-[#202124]">Auto-Reply</span>
                  <button
                    onClick={() => updatePlan(key, 'auto_reply', !plan.auto_reply)}
                    className={`w-10 h-5 rounded-full transition-colors ${
                      plan.auto_reply ? 'bg-[#1E8E3E]' : 'bg-[#DADCE0]'
                    }`}
                    data-testid={`${key}-auto-reply`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      plan.auto_reply ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminMembershipPage;
